












































import Vue from "vue";
import Component from "vue-class-component";
import PowerBiExport from "@/components/report/PowerBiExport.vue";
import {Prop, Watch} from "vue-property-decorator";
import {Getter} from "vuex-class";
import * as pbi from "powerbi-client";
import _ from "lodash";
import {ElTabPane} from "element-ui/types/tab-pane";
import {DisplayOption} from "powerbi-models";

@Component({
    components: {
        PowerBiExport,
    },
})
export default class PowerBiFrame extends Vue {
    @Prop({default: null}) public token!: string | null;
    @Prop({default: null}) public embedUuid!: string | null;
    @Prop({default: null}) public label!: string | null;
    @Prop({default: null}) public hiddenPages!: [] | null;
    @Prop({default: true}) public exportable!: boolean;
    @Prop({default: false}) public isWidget!: boolean;
    @Prop({default: false}) public reportUuid!: string;
    @Prop({default: null}) public defaultPage!: string | null;
    @Prop({default: false}) public homepage!: boolean;
    @Prop({default: null}) public clientReportId!: string | null;

    @Getter("getCurrentOwnerUuid") public currentOwnerUuid!: string;

    private powerBi: pbi.service.Service | null = null;
    private report: pbi.Report | null = null;
    private activePage: string = "";
    private pagesAvailable: pbi.Page[] = [];
    private pagesVisible: boolean = false;
    private loading: boolean = false;
    private refreshInterval: number | null = null;

    @Watch("currentOwnerUuid") protected currentOwnerUuidChanged(): void {
        this.loading = true;
        this.loadPowerBiReport();
    }

    private mounted() {
        this.loadPowerBiReport();
    }

    @Watch("embedUuid") public onReportChanged() {
        this.loadPowerBiReport();
    }

    @Watch("clientReportId") public onClientReportIdChanged() {
        this.loadPowerBiReport();
    }

    @Watch("token") public async onTokenChanged(newToken: string | null) {
        if (this.report && newToken) {
            // Just refresh the token without reloading report
            await this.report.setAccessToken(newToken);
        }
    }

    public loadPowerBiReport(): void {
        if (!this.embedUuid || !this.token) {
            return;
        }

        if (!this.powerBi) {
            this.powerBi = new pbi.service.Service(
                pbi.factories.hpmFactory,
                pbi.factories.wpmpFactory,
                pbi.factories.routerFactory
            );
        }

        const powerBiElement = _.get(this.$refs, "power-bi-report") as HTMLElement;
        if (this.report) {
            // Report.off removes a given event handler if it exists.
            this.report.off("loaded");
            this.powerBi.reset(powerBiElement);
        }
        this.pagesVisible = false;
        this.report = this.powerBi.embed(
            powerBiElement,
            {
                settings: {
                    layoutType: pbi.models.LayoutType.Custom,
                    customLayout: {
                        displayOption: this.homepage ? pbi.models.DisplayOption.FitToPage : DisplayOption.ActualSize
                    },
                    navContentPaneEnabled: false,
                },
                id: this.embedUuid,
                type: "report",
                accessToken: this.token,
                tokenType: pbi.models.TokenType.Embed,
                embedUrl: "https://app.powerbi.com/reportEmbed"
            } as pbi.IEmbedConfiguration
        ) as pbi.Report;

        this.report.on("loaded", () => {
            // Report.on will add an event handler which prints to Log window.
            this.loading = false;
            this.$emit("loaded");
            this.loadPages();
            this.startTokenRefreshTimer();
        });
    }

    private loadPages(): void {
        if (!this.report) {
            return;
        }

        this.pagesAvailable = [];
        this.report.getPages().then((pages: pbi.Page[]) => {
            _.forEach(pages, (page: pbi.Page, idx) => {
                // if a page is hidden, visibility = 1
                if (0 === page.visibility) {
                    if (-1 === _.indexOf(this.hiddenPages, page.displayName)) {
                        this.pagesAvailable.push(page);
                    }
                }
            });

            const matchingPage = _.find(
                this.pagesAvailable,
                (page) => page.displayName === this.defaultPage
            );

            if (matchingPage) {
                this.changePage(matchingPage.name);
            } else {
                this.changePage(this.pagesAvailable[0].name);
            }

            this.pagesVisible = true;
        });
    }

    private tabClick(tab: ElTabPane) {
        this.changePage(tab.name, false);
    }

    private changePage(pageName: string, timeout = true): void {
        if (!this.report || pageName === this.activePage) {
            return;
        }

        const page = this.report.page(pageName);
        this.activePage = pageName;
        setTimeout(() => {
            page.setActive();
        }, timeout ? 500 : 0);
    }

    private get frameClasses(): object {
        return {
            "pbi-frame": true,
            "pbi-frame--widget": this.isWidget,
        };
    }

    private startTokenRefreshTimer(): void {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }

        this.refreshInterval = window.setInterval(() => {
            this.$emit("refresh-token-needed");
        }, 5 * 60 * 1000); // every 5 minutes
    }

    private beforeDestroy(): void {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }
}
